import React, { useMemo, useState } from 'react';
import { createCn } from '../../../helpers/utils';
import {
  ConversationMetrics,
  EmotionAnalysis,
  FillerStats,
  KeywordTheme,
  MeetingInsights,
  ProsodyAnalysis,
  SlideTimelineEntry,
  SpeechSegment,
} from '../../../services/api.service';
import { formatSecondsShort } from '../utils/audio-segments';
import {
  buildActivityBuckets,
  buildInterruptions,
  buildReplicaStats,
  buildTonePeriods,
  getMomentTagStyle,
  getToneColor,
  resolveSpeakerColor,
  resolveSpeakerDisplayName,
  resolveSpeakerFullName,
  sortSpeakerTones,
} from '../utils/meeting-analytics';
import KeywordsPanel from './keywords-panel.component';
import AdvancedMetricsPanel from './advanced-metrics-panel.component';
import BenchmarkInsights from './benchmark-insights.component';
import MeetingRibbon from './meeting-ribbon.component';
import VoiceAnalyticsCards from './voice-analytics-cards.component';
import { formatTopicTime } from './speaker-donut-chart.component';
import classes from '../styles.module.scss';

const cn = createCn('file', classes);

type SpeakerTimeline = {
  name: string;
  color: string;
};

type Props = {
  segments: SpeechSegment[];
  speakerTimelines: SpeakerTimeline[];
  durationSec: number;
  insights: MeetingInsights | null;
  slideTimeline?: SlideTimelineEntry[];
  isVideoMeeting?: boolean;
  insightsLoading: boolean;
  onSeek: (seconds: number) => void;
  onRefreshInsights: () => void;
  onOpenThemeReplicas?: (theme: KeywordTheme) => void;
  onOpenKeywordReplicas?: (word: string) => void;
  /** Задача F1: голосовая аналитика (H1/B1/B2) — null у встреч, обработанных до этих задач. */
  conversationMetrics?: ConversationMetrics | null;
  fillerStats?: FillerStats | null;
  emotionAnalysis?: EmotionAnalysis | null;
  prosodyAnalysis?: ProsodyAnalysis | null;
  recomputingAnalytics?: boolean;
  onRecomputeAnalytics?: () => void;
};

export function AnalyticsCard({
  title,
  badge,
  defaultOpen = false,
  children,
}: {
  title: string;
  badge: 'data' | 'ai' | 'vlm';
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const badgeLabel =
    badge === 'vlm' ? 'ИИ · VLM' : badge === 'ai' ? 'ИИ-аналитика' : 'из данных';

  return (
    <div className={cn('analytics-card', { collapsed: !open })}>
      <button
        type="button"
        className={cn('analytics-card-head', { clickable: true })}
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
      >
        <span className={cn('analytics-card-chevron')}>{open ? '▾' : '▸'}</span>
        <span className={cn('analytics-card-title')}>{title}</span>
        <span
          className={cn('analytics-card-badge', {
            ai: badge === 'ai',
            vlm: badge === 'vlm',
            data: badge === 'data',
          })}
        >
          {badgeLabel}
        </span>
      </button>
      {open && children}
    </div>
  );
}

const MeetingAnalyticsPanel: React.FC<Props> = ({
  segments,
  speakerTimelines,
  durationSec,
  insights,
  slideTimeline = [],
  isVideoMeeting = false,
  insightsLoading,
  onSeek,
  onRefreshInsights,
  onOpenThemeReplicas,
  onOpenKeywordReplicas,
  conversationMetrics = null,
  fillerStats = null,
  emotionAnalysis = null,
  prosodyAnalysis = null,
  recomputingAnalytics = false,
  onRecomputeAnalytics,
}) => {
  const speakerColorMap = useMemo(() => {
    const map: Record<string, string> = {};
    speakerTimelines.forEach(s => { map[s.name] = s.color; });
    return map;
  }, [speakerTimelines]);

  const activityBuckets = useMemo(
    () => buildActivityBuckets(segments, speakerColorMap, durationSec),
    [segments, speakerColorMap, durationSec],
  );

  const replicaStats = useMemo(
    () => buildReplicaStats(segments, speakerTimelines),
    [segments, speakerTimelines],
  );

  const interruptions = useMemo(
    () => buildInterruptions(segments, speakerColorMap),
    [segments, speakerColorMap],
  );

  const tonePeriods = useMemo(
    () => (
      insights
        ? buildTonePeriods(
            insights.tone_segments,
            durationSec,
            4,
            insights.key_moments ?? [],
          )
        : []
    ),
    [insights, durationSec],
  );

  const maxActivity = useMemo(
    () => Math.max(...activityBuckets.map(b => b.total), 1),
    [activityBuckets],
  );

  const maxInterruptions = interruptions[0]?.count || 1;

  const speakerTones = useMemo(
    () => (
      insights?.speaker_tones?.length
        ? sortSpeakerTones(insights.speaker_tones, speakerTimelines)
        : []
    ),
    [insights, speakerTimelines],
  );

  const hasAiContent = insights && (
    insights.key_moments?.length ||
    insights.key_topics?.length ||
    insights.keywords?.length ||
    insights.themes?.length ||
    insights.action_items?.length ||
    insights.speaker_tones?.length ||
    insights.kpi_table?.length ||
    insights.decisions?.length ||
    insights.conflicts?.length ||
    (insights.tone_segments?.length ?? 0) > 0
  );

  return (
    <div className={cn('analytics-panel')}>
      <div className={cn('analytics-panel-head')}>
        <span className={cn('analytics-panel-title')}>
          {isVideoMeeting ? 'Речевая и визуальная аналитика' : 'Речевая аналитика'}
        </span>
        <button
          type="button"
          className={cn('analytics-refresh-btn')}
          onClick={onRefreshInsights}
          disabled={insightsLoading}
        >
          {insightsLoading ? 'Анализ… 1–3 мин' : 'Обновить анализ'}
        </button>
      </div>

      <MeetingRibbon
        segments={segments}
        speakerTimelines={speakerTimelines}
        durationSec={durationSec}
        toneSegments={insights?.tone_segments ?? []}
        keyMoments={insights?.key_moments ?? []}
        emotionTimeline={emotionAnalysis?.timeline ?? []}
        onSeek={onSeek}
      />

      <BenchmarkInsights
        segments={segments}
        speakerTimelines={speakerTimelines}
        durationSec={durationSec}
        insights={insights}
      />

      <VoiceAnalyticsCards
        segments={segments}
        speakerTimelines={speakerTimelines}
        conversationMetrics={conversationMetrics}
        fillerStats={fillerStats}
        emotionAnalysis={emotionAnalysis}
        prosodyAnalysis={prosodyAnalysis}
        recomputingAnalytics={recomputingAnalytics}
        onRecomputeAnalytics={onRecomputeAnalytics ?? (() => {})}
      />

      {(insights?.summary_cards?.length ?? 0) > 0 && (
        <div className={cn('analytics-summary-grid')}>
          {insights!.summary_cards!.map(card => (
            <div key={card.id} className={cn('analytics-summary-card')}>
              <span className={cn('analytics-summary-label')}>{card.label}</span>
              <span className={cn('analytics-summary-value')}>{card.value}</span>
              {card.detail && (
                <span className={cn('analytics-summary-detail')}>{card.detail}</span>
              )}
            </div>
          ))}
        </div>
      )}

      <AnalyticsCard title="Активность реплик по времени" badge="data" defaultOpen>
        <div className={cn('activity-chart')}>
          {activityBuckets.map((bucket, i) => (
            <button
              key={i}
              type="button"
              className={cn('activity-bar-wrap')}
              title={`${formatSecondsShort(bucket.startSec)} — ${bucket.total} репл.`}
              onClick={() => onSeek(bucket.startSec)}
            >
              <div
                className={cn('activity-bar')}
                style={{ height: `${Math.max((bucket.total / maxActivity) * 100, 4)}%` }}
              >
                {bucket.bySpeaker.map(s => (
                  <div
                    key={s.name}
                    style={{
                      flex: s.count,
                      backgroundColor: s.color,
                    }}
                  />
                ))}
              </div>
            </button>
          ))}
        </div>
        <div className={cn('activity-labels')}>
          <span>0:00</span>
          <span>{formatSecondsShort(durationSec)}</span>
        </div>
        <p className={cn('analytics-hint')}>Высота столбца = число реплик. Клик — перемотка.</p>
      </AnalyticsCard>

      <AnalyticsCard title="Расширенная аналитика" badge="data" defaultOpen={false}>
        <AdvancedMetricsPanel
          segments={segments}
          durationSec={durationSec}
          speakerTimelines={speakerTimelines}
          onSeek={onSeek}
        />
      </AnalyticsCard>

      <AnalyticsCard title="Реплики по спикерам" badge="data" defaultOpen={false}>
        <div className={cn('replica-chips')}>
          {replicaStats.map(s => (
            <div key={s.name} className={cn('replica-chip')}>
              <span
                className={cn('replica-chip-stripe')}
                style={{ backgroundColor: s.color }}
                aria-hidden
              />
              <span className={cn('replica-chip-name')}>
                <span
                  className={cn('replica-chip-dot')}
                  style={{ backgroundColor: s.color }}
                  aria-hidden
                />
                {s.displayName}
              </span>
              <span className={cn('replica-chip-count')}>{s.count}</span>
              <span className={cn('replica-chip-label')}>реплик</span>
            </div>
          ))}
        </div>
      </AnalyticsCard>

      {hasAiContent && tonePeriods.length > 0 && (
        <AnalyticsCard title="Тональность по времени" badge="ai">
          {insights?.tone_summary && (
            <p className={cn('analytics-subtitle')}>
              {insights.tone_summary.label}
              {(insights.tone_summary.tension_count ?? 0) > 0 && (
                <> · эпизодов напряжения: {insights.tone_summary.tension_count}</>
              )}
              {(insights.tone_summary.tension_count ?? 0) > 0
                ? ' · выявлены эпизоды напряжения по репликам'
                : ' · по тексту реплик'}
            </p>
          )}
          <div className={cn('tone-periods')}>
            {tonePeriods.map(period => (
              <div key={period.label} className={cn('tone-period-row')}>
                <span className={cn('tone-period-label')}>{period.label}</span>
                <div className={cn('tone-period-bar')}>
                  {period.segments.map((seg, i) => (
                    <div
                      key={i}
                      style={{
                        width: `${seg.widthPct}%`,
                        backgroundColor: getToneColor(seg.tone),
                      }}
                    />
                  ))}
                </div>
                <span className={cn('tone-period-mood')}>{period.moodLabel}</span>
              </div>
            ))}
          </div>
        </AnalyticsCard>
      )}

      {hasAiContent && (insights?.key_topics?.length ?? 0) > 0 && (
        <AnalyticsCard title="Ключевые темы" badge="ai">
          <div className={cn('topics-list')}>
            {insights!.key_topics!.map((topic, i) => (
              <button
                key={`${topic.time}-${i}`}
                type="button"
                className={cn('topics-item')}
                onClick={() => onSeek(topic.time)}
              >
                <span className={cn('topics-num')}>{i + 1}</span>
                <span className={cn('topics-text')}>{topic.topic}</span>
                <span className={cn('topics-time')}>{formatTopicTime(topic.time)}</span>
              </button>
            ))}
          </div>
        </AnalyticsCard>
      )}

      {hasAiContent && (insights?.action_items?.length ?? 0) > 0 && (
        <AnalyticsCard title="Поручения" badge="ai">
          <div className={cn('action-items-list')}>
            {insights!.action_items!.map((item, i) => (
              <button
                key={`${item.time}-${i}`}
                type="button"
                className={cn('action-items-item')}
                onClick={() => onSeek(item.time)}
              >
                <span className={cn('action-items-time')}>{formatTopicTime(item.time)}</span>
                <div className={cn('action-items-body')}>
                  <span className={cn('action-items-text')}>{item.text}</span>
                  {(item.assignee || item.deadline) && (
                    <span className={cn('action-items-meta')}>
                      {item.assignee && <span>{item.assignee}</span>}
                      {item.assignee && item.deadline && <span> · </span>}
                      {item.deadline && <span>{item.deadline}</span>}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </AnalyticsCard>
      )}

      {hasAiContent && speakerTones.length > 0 && (
        <AnalyticsCard title="Тональность по спикерам" badge="ai">
          <p className={cn('analytics-subtitle')}>По тексту реплик каждого участника</p>
          <div className={cn('speaker-tones-list')}>
            {speakerTones.map(item => {
              const color = resolveSpeakerColor(item.speaker, speakerTimelines);
              const displayName = resolveSpeakerDisplayName(item.speaker, speakerTimelines);
              const fullName = resolveSpeakerFullName(item.speaker, speakerTimelines);
              const score = item.score ?? 40;
              return (
                <div key={item.speaker} className={cn('speaker-tones-row')}>
                  <div className={cn('speaker-tones-label')}>
                    <span className={cn('speaker-tones-dot')} style={{ backgroundColor: color }} />
                    <span className={cn('speaker-tones-name')} title={fullName}>{displayName}</span>
                  </div>
                  <div className={cn('speaker-tones-bar-track')}>
                    <div
                      className={cn('speaker-tones-bar-fill')}
                      style={{
                        width: `${Math.max(score, 8)}%`,
                        backgroundColor: getToneColor(item.tone),
                      }}
                    />
                  </div>
                  <span
                    className={cn('speaker-tones-badge')}
                    style={{
                      color: getToneColor(item.tone),
                      backgroundColor: `${getToneColor(item.tone)}18`,
                    }}
                  >
                    {item.label}
                  </span>
                </div>
              );
            })}
          </div>
        </AnalyticsCard>
      )}

      {insights?.agreement_index && (
        <AnalyticsCard title="Индекс согласия" badge="data">
          <p className={cn('analytics-kpi-value')}>
            {insights.agreement_index.agreement_index}/100
            <span className={cn('analytics-kpi-label')}> · {insights.agreement_index.label}</span>
          </p>
          {(insights.agreement_index.top_interruption_pairs?.length ?? 0) > 0 && (
            <p className={cn('analytics-subtitle')}>
              Перебивания:{' '}
              {insights.agreement_index.top_interruption_pairs!.map(p =>
                `${p.from}→${p.to} (${p.count})`,
              ).join(', ')}
            </p>
          )}
        </AnalyticsCard>
      )}

      {(slideTimeline?.length ?? 0) > 0 && !isVideoMeeting && (
        <AnalyticsCard title="Слайды и экран" badge="vlm">
          <p className={cn('analytics-hint')}>
            Распознано VLM-моделями Qwen по ключевым кадрам видео. Клик — перемотка к моменту.
          </p>
          <div className={cn('moments-list')}>
            {slideTimeline!.map((slide, i) => (
              <button
                key={`slide-${slide.slide_index ?? i}`}
                type="button"
                className={cn('moments-item')}
                onClick={() => onSeek(slide.time_sec ?? 0)}
              >
                <span className={cn('moments-time')}>{slide.timestamp || '0:00'}</span>
                <span className={cn('moments-dot')} />
                <span className={cn('moments-text')}>
                  {slide.title || slide.content_type || 'Кадр'}
                  {slide.bullets?.length ? (
                    <span className={cn('analytics-subtitle')}>
                      {' '}· {slide.bullets.slice(0, 2).join('; ')}
                    </span>
                  ) : null}
                  {slide.ocr_text && !slide.title && (
                    <span className={cn('analytics-subtitle')}> · {slide.ocr_text.slice(0, 80)}</span>
                  )}
                  {slide.error && (
                    <span className={cn('analytics-subtitle')}> · {slide.error}</span>
                  )}
                </span>
                {slide.is_scene_change && (
                  <span className={cn('analytics-card-badge', { data: true })}>сцена</span>
                )}
              </button>
            ))}
          </div>
        </AnalyticsCard>
      )}

      {(insights?.engagement_by_speaker?.length ?? 0) > 0 && (
        <AnalyticsCard title="Индекс вовлечённости" badge="data">
          <p className={cn('analytics-subtitle')}>Инициативные реплики × длина (не просто счётчик)</p>
          <table className={cn('analytics-table')}>
            <thead>
              <tr>
                <th>Участник</th>
                <th>Индекс</th>
                <th>Реплик</th>
                <th>Иниц.</th>
              </tr>
            </thead>
            <tbody>
              {insights!.engagement_by_speaker!.map(row => (
                <tr key={row.speaker}>
                  <td>{row.speaker}</td>
                  <td><strong>{row.engagement_index}</strong></td>
                  <td>{row.replica_count}</td>
                  <td>{row.initiative_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </AnalyticsCard>
      )}

      {(insights?.kpi_table?.length ?? 0) > 0 && (
        <AnalyticsCard title="Финансовые показатели" badge="ai">
          <table className={cn('analytics-table')}>
            <thead>
              <tr>
                <th>Показатель</th>
                <th>План</th>
                <th>Факт</th>
                <th>Δ%</th>
                <th>Время</th>
              </tr>
            </thead>
            <tbody>
              {insights!.kpi_table!.map((row, i) => (
                <tr key={`${row.metric}-${i}`}>
                  <td>{row.metric}</td>
                  <td>{row.plan ?? '—'}</td>
                  <td>{row.fact ?? '—'}</td>
                  <td>{row.variance_pct != null ? `${row.variance_pct > 0 ? '+' : ''}${row.variance_pct}%` : '—'}</td>
                  <td>{row.timestamp || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </AnalyticsCard>
      )}

      {(insights?.decisions?.length ?? 0) > 0 && (
        <AnalyticsCard title="Принятые решения" badge="ai">
          <div className={cn('moments-list')}>
            {insights!.decisions!.map((item, i) => (
              <button
                key={`dec-${i}`}
                type="button"
                className={cn('moments-item')}
                onClick={() => onSeek(item.time)}
              >
                <span className={cn('moments-time')}>{formatTopicTime(item.time)}</span>
                <span className={cn('moments-dot')} style={{ backgroundColor: 'var(--color-primary-deep)' }} />
                <span className={cn('moments-text')}>{item.text}</span>
                {item.speaker && (
                  <span className={cn('moments-tag')} style={{ color: 'var(--color-primary-deep)', backgroundColor: 'var(--color-nav-active)' }}>
                    {item.speaker}
                  </span>
                )}
              </button>
            ))}
          </div>
        </AnalyticsCard>
      )}

      {(insights?.conflicts?.length ?? 0) > 0 && (
        <AnalyticsCard title="Споры и конфликты" badge="ai">
          <div className={cn('moments-list')}>
            {insights!.conflicts!.map((item, i) => {
              const tagStyle = getMomentTagStyle(item.tag_type || 'conflict');
              return (
                <button
                  key={`conf-${i}`}
                  type="button"
                  className={cn('moments-item')}
                  onClick={() => onSeek(item.time)}
                >
                  <span className={cn('moments-time')}>{formatTopicTime(item.time)}</span>
                  <span className={cn('moments-dot')} style={{ backgroundColor: tagStyle.dot }} />
                  <span className={cn('moments-text')}>{item.text}</span>
                  <span
                    className={cn('moments-tag')}
                    style={{ color: tagStyle.text, backgroundColor: tagStyle.bg, borderColor: tagStyle.border }}
                  >
                    {item.tag || 'конфликт'}
                  </span>
                </button>
              );
            })}
          </div>
        </AnalyticsCard>
      )}

      {hasAiContent && (insights?.key_moments?.length ?? 0) > 0 && (
        <AnalyticsCard title="Ключевые моменты" badge="ai">
          <div className={cn('moments-list')}>
            {insights!.key_moments!.map((moment, i) => {
              const tagStyle = getMomentTagStyle(moment.tag_type);
              return (
                <button
                  key={`${moment.time}-${i}`}
                  type="button"
                  className={cn('moments-item')}
                  onClick={() => onSeek(moment.time)}
                >
                  <span className={cn('moments-time')}>{formatTopicTime(moment.time)}</span>
                  <span
                    className={cn('moments-dot')}
                    style={{ backgroundColor: tagStyle.dot }}
                  />
                  <span className={cn('moments-text')}>{moment.text}</span>
                  <span
                    className={cn('moments-tag')}
                    style={{
                      color: tagStyle.text,
                      backgroundColor: tagStyle.bg,
                      borderColor: tagStyle.border,
                    }}
                  >
                    {moment.tag}
                  </span>
                </button>
              );
            })}
          </div>
        </AnalyticsCard>
      )}

      {(insights?.keywords?.length ?? 0) > 0 && (
        <AnalyticsCard title="Ключевые слова" badge="ai">
          <KeywordsPanel
            keywords={insights!.keywords!}
            themes={insights?.themes}
            segments={segments}
            speakerTimelines={speakerTimelines}
            onSeek={onSeek}
            onOpenThemeReplicas={onOpenThemeReplicas}
            onOpenKeywordReplicas={onOpenKeywordReplicas}
          />
        </AnalyticsCard>
      )}

      {interruptions.length > 0 && (
        <AnalyticsCard title="Перебивания" badge="data">
          <p className={cn('analytics-subtitle')}>Кто кого перебивал чаще всего</p>
          <div className={cn('interruptions-list')}>
            {interruptions.map(item => (
              <div key={`${item.from}-${item.to}`} className={cn('interruptions-row')}>
                <span className={cn('interruptions-pair')}>
                  <span style={{ color: item.fromColor }} title={item.fromFull}>{item.fromDisplay}</span>
                  <span className={cn('interruptions-arrow')}>→</span>
                  <span style={{ color: item.toColor }} title={item.toFull}>{item.toDisplay}</span>
                </span>
                <div className={cn('interruptions-bar-track')}>
                  <div
                    className={cn('interruptions-bar-fill')}
                    style={{
                      width: `${(item.count / maxInterruptions) * 100}%`,
                      background: `linear-gradient(90deg, ${item.fromColor}, ${item.toColor})`,
                    }}
                  />
                </div>
                <span className={cn('interruptions-count')}>{item.count}</span>
              </div>
            ))}
          </div>
        </AnalyticsCard>
      )}
    </div>
  );
};

export default MeetingAnalyticsPanel;
